import { WebShopIcon } from "@/src/components/WebShopIcon";
import {
    Unstable_NumberInput as NumberInput,
    NumberInputOwnerState,
} from "@mui/base/Unstable_NumberInput";
import { Box, Stack, Typography } from "@mui/material";
import clsx from "clsx";
import { FieldValues, useController } from "react-hook-form";
import { NumberFieldProps } from "../lib/type";
import {
    getDecrementButtonStyles,
    getIncrementButtonStyles,
    getInputStyles,
    getRootStyles,
} from "../lib/utils";
import {validationRules} from "@/src/shared/utils/validationRules";

export const NumberField = <T extends FieldValues>({
    className,
    control,
    name,
    slotProps,
    min,
    max,
    toggleHintArea,
    closeHintArea,
    openHintArea,
    hintIcon,
    disabled,
    required,
    deps,
    onChange: onChangeController,
    ...rest
}: NumberFieldProps<T>) => {
    const {
        field: { onChange, value = 0, ref },
        fieldState: { error: fieldError },
    } = useController({ control, name,
        rules: validationRules.NumberInput({ required, disabled, min, max, deps }),
    });

    return (
        <Stack className={"flex w-full flex-col items-start"}>
            <NumberInput
                {...rest}
                className={clsx("w-full", className, {
                    "cursor-default pointer-events-none": disabled,
                })}
                ref={ref}
                value={value}
                min={min}
                max={max}
                disabled={disabled}
                onChange={(e, value) => {
                    onChange(value)
                    if (onChangeController) {
                        onChangeController(e, value)
                    }
                }}
                endAdornment={
                    hintIcon && (
                        <Box
                            className={"relative cursor-pointer md:hidden"}
                            onClick={toggleHintArea}
                        >
                            <WebShopIcon
                                name={"questionCircle"}
                                className={"absolute top-[5px] left-[-25px]"}
                            />
                        </Box>
                    )
                }
                slotProps={{
                    root: (ownerState: NumberInputOwnerState) =>
                        getRootStyles(ownerState, slotProps, !!fieldError, disabled),
                    input: (ownerState: NumberInputOwnerState) =>
                        getInputStyles(ownerState, slotProps),
                    incrementButton: (ownerState: NumberInputOwnerState) =>
                        getIncrementButtonStyles(ownerState, slotProps),
                    decrementButton: (ownerState: NumberInputOwnerState) =>
                        getDecrementButtonStyles(ownerState, slotProps),
                }}
            />
            {fieldError && (
                <Typography
                    className={"text-red ml-[5px] mr-[14px] mt-[3px] text-xs font-normal"}
                >
                    {fieldError.message}
                </Typography>
            )}
        </Stack>
    );
};
